<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Order Number Input -->
              <base-input
                col="4"
                type="number"
                :placeholder="$t('PLACEHOLDERS.order_number')"
                v-model.trim="filterOptions.order_id"
              />
              <!-- End:: Order Number Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="4"
                :optionsList="orderStatuses"
                :placeholder="$t('PLACEHOLDERS.order_status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->

              <!-- Start:: Client Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.client_name')"
                v-model.trim="filterOptions.user_name"
              />
              <!-- End:: Client Name Input -->

              <!-- Start:: Stable Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.stable_name')"
                v-model.trim="filterOptions.provider_name"
              />
              <!-- End:: Stable Name Input -->

              <!-- Start:: Date From Input -->
              <base-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_from')"
                v-model="filterOptions.date_from"
              />
              <!-- End:: Date From Input -->

              <!-- Start:: Date To Input -->
              <base-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_to')"
                v-model="filterOptions.date_to"
              />
              <!-- End:: Date To Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.room_rent_orders") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <button
            class="export_btn"
            @click="exportToExcel"
            :disabled="isWaitingRequest"
          >
            <i class="fal fa-file-excel"></i>
            {{ $t("BUTTONS.export_excel") }}
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Item Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Item Serial Number -->

        <!-- Start:: Order Number -->
        <template v-slot:[`item.order_id`]="{ item }">
          <h6 class="text-primary">#{{ item.id }}</h6>
        </template>
        <!-- End:: Order Number -->

        <!-- Start:: Order Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <h6>{{ formatDate(item.created_at) }}</h6>
        </template>
        <!-- End:: Order Date -->

        <!-- Start:: Order Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            text-color="white"
            small
          >
            {{ item.status_label }}
          </v-chip>
        </template>
        <!-- End:: Order Status -->

        <!-- Start:: Client Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.user_name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user_name }}</h6>
        </template>
        <!-- End:: Client Name -->

        <!-- Start:: Stable Name -->
        <template v-slot:[`item.provider_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.provider_name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.provider_name }}</h6>
        </template>
        <!-- End:: Stable Name -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllRoomRentOrders",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    orderStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.new"),
          value: "new",
        },
        {
          id: 2,
          name: this.$t("STATUS.accepted"),
          value: "accepted",
        },
        {
          id: 3,
          name: this.$t("STATUS.rejected"),
          value: "rejected",
        },
        {
          id: 4,
          name: this.$t("STATUS.current"),
          value: "in_progress",
        },
        {
          id: 5,
          name: this.$t("STATUS.completed"),
          value: "completed",
        },
        {
          id: 6,
          name: this.$t("STATUS.cancelled"),
          value: "cancelled",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        order_id: null,
        status: null,
        user_name: null,
        provider_name: null,
        date_from: null,
        date_to: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.order_number"),
          value: "order_id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.order_date"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.order_status"),
          value: "status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.client_name"),
          value: "user_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.stable_name"),
          value: "provider_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      exportUrl: null,
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Helper Methods
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString(this.getAppLocale === 'ar' ? 'ar-EG' : 'en-US');
    },

    getStatusColor(status) {
      const statusColors = {
        new: 'blue',
        accepted: 'green',
        rejected: 'red',
        current: 'orange',
        completed: 'teal',
        cancelled: 'grey'
      };
      return statusColors[status] || 'grey';
    },
    // End:: Helper Methods

    // Start:: Export to Excel
    async exportToExcel() {
      if (this.exportUrl) {
        window.open(this.exportUrl, '_blank');
      } else {
        this.$message.warning(this.$t("MESSAGES.no_export_data"));
      }
    },
    // End:: Export to Excel

    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/room-rent/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.order_id = null;
      this.filterOptions.status = null;
      this.filterOptions.user_name = null;
      this.filterOptions.provider_name = null;
      this.filterOptions.date_from = null;
      this.filterOptions.date_to = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/room-rent/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Data
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "orders",
          params: {
            type: "room_rent",
            page: this.paginations.current_page,
            id: this.filterOptions.order_id,
            status: this.filterOptions.status?.value,
            user_name: this.filterOptions.user_name,
            provider_name: this.filterOptions.provider_name,
            created_at_from: this.filterOptions.date_from,
            created_at_to: this.filterOptions.date_to,
          },
        });
        
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.exportUrl = res.data.data.export_url;
        this.paginations.last_page = res.data.data.meta?.last_page || 1;
        this.paginations.items_per_page = res.data.data.meta?.per_page || 6;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(error.response?.data?.message || this.$t("MESSAGES.somethingWentWrong"));
      }
    },
    // End:: Set Table Rows

    // ==================== Start:: Crud ====================
    // ===== Start:: Show
    showItem(item) {
      this.$router.push({ path: `/room-rent/show/${item.id}` });
    },
    // ===== End:: Show
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.export_btn {
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.export_btn:hover {
  background: #218838;
}

.export_btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}
</style>